import { useEffect, useState } from 'react';
import { ApiError, type Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { DangerButton, Field, ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';

/** The part of the agent spec the business edits here (the API validates everything again). */
export type Fact = { key: string; label: string; type: string; enumValues?: string[] | undefined; required: boolean };
export type EditableSpec = {
  identity: { name: string; brandName: string; persona: string; legalName?: string };
  callPurpose?: 'commercial' | 'informational';
  mission: { goal: string; successCondition: string; audience: string };
  style: { tone: string; maxSentencesPerTurn: number; maxQuestionsPerTurn: number; humor?: string };
  facts: Fact[];
  boundaries: { blockedTopics: string[]; mustNotPromise: string[] };
  compliance: { require: Array<{ id: string; instruction: string; withinSeconds?: number }>; forbid: unknown[] };
  languages: { primary: string };
  [k: string]: unknown;
};

const FACT_TYPES = ['text', 'boolean', 'number', 'enum', 'datetime', 'phone', 'email'] as const;

/** Latin letters that have no accent decomposition. */
const FOLD: Record<string, string> = { ı: 'i', ß: 'ss', ø: 'o', æ: 'ae', œ: 'oe', đ: 'd', ł: 'l', þ: 'th' };
/** A snake_case key from a label (letters without accents); falls back to a numbered key. */
export const keyFrom = (label: string, taken: string[]): string => {
  const base = label.toLowerCase().replace(/[ıßøæœđłþ]/gu, (c) => FOLD[c] ?? c).normalize('NFKD').replace(/\p{M}/gu, '').replace(/[^a-z0-9]+/gu, '_').replace(/^_+|_+$/gu, '').slice(0, 40);
  let k = /^[a-z][a-z0-9_]{1,}$/u.test(base) ? base : `fact_${taken.length + 1}`;
  let n = 2;
  while (taken.includes(k)) k = `${base || 'fact'}_${n++}`;
  return k;
};

const lines = (s: string): string[] => s.split('\n').map((x) => x.trim()).filter(Boolean);
export const errorText = (t: T, e: unknown): string => {
  const code = e instanceof ApiError ? e.code : 'generic';
  return t(`error.${code}`) === `error.${code}` ? `${t('error.generic')} (${code})` : t(`error.${code}`);
};

/** Save the tab's draft as one spec edit; the parent reloads (banner, other tabs). */
export const useSpecSave = (api: Api, agentId: string, onSaved: () => void, t: T) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (patch: Record<string, unknown>, commit: () => void) => {
    setSaving(true);
    setError(null);
    try {
      await api.saveSpec(agentId, patch);
      commit();
      onSaved();
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, save };
};

export const GeneralPanel = ({ api, t, agentId, spec, onSaved }: { api: Api; t: T; agentId: string; spec: EditableSpec; onSaved: () => void }) => {
  // ADR-071: the spec is saved with the Agents permission; without it the form is read-only (the note sits at the tab top).
  const canEdit = useCan()(PERM.agents);
  const d = useDraft({ identity: spec.identity, mission: spec.mission, style: spec.style, facts: spec.facts });
  const s = useSpecSave(api, agentId, onSaved, t);
  const v = d.value;
  const set = (patch: Partial<typeof v>) => d.set({ ...v, ...patch });
  const setFact = (i: number, f: Partial<Fact>) => set({ facts: v.facts.map((x, j) => (j === i ? { ...x, ...f } : x)) });
  const payload = () => ({
    ...v,
    // New rows get a key from their label.
    facts: v.facts
      .filter((f) => f.label.trim())
      .map((f, i, all) => (f.key.startsWith('fact_') ? { ...f, key: keyFrom(f.label, all.filter((_, j) => j !== i).map((x) => x.key)) } : f)),
  });

  return (
    <div className="settings">
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <Section title={t('settings.identity')} description={t('settings.identity.hint')}>
          <div className="form-grid">
            <Field label={t('field.identity.name')}><input value={v.identity.name} onChange={(e) => set({ identity: { ...v.identity, name: e.target.value } })} /></Field>
            <Field label={t('field.identity.brandName')}><input value={v.identity.brandName} onChange={(e) => set({ identity: { ...v.identity, brandName: e.target.value } })} /></Field>
          </div>
        </Section>
        <Section title={t('settings.personality')} description={t('settings.personality.hint')}>
          <Field label={t('field.identity.persona')}><textarea rows={2} value={v.identity.persona} onChange={(e) => set({ identity: { ...v.identity, persona: e.target.value } })} /></Field>
          <div className="form-grid">
            <Field label={t('field.style.tone')}><input value={v.style.tone} onChange={(e) => set({ style: { ...v.style, tone: e.target.value } })} /></Field>
            <Field label={t('settings.maxSentences')} hint={t('settings.maxSentences.hint')}>
              <select value={v.style.maxSentencesPerTurn} onChange={(e) => set({ style: { ...v.style, maxSentencesPerTurn: Number(e.target.value) } })}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          </div>
        </Section>
        <Section title={t('settings.mission')} description={t('settings.mission.hint')}>
          <Field label={t('field.mission.goal')}><textarea rows={2} value={v.mission.goal} onChange={(e) => set({ mission: { ...v.mission, goal: e.target.value } })} /></Field>
          <Field label={t('field.mission.successCondition')}><textarea rows={2} value={v.mission.successCondition} onChange={(e) => set({ mission: { ...v.mission, successCondition: e.target.value } })} /></Field>
          <Field label={t('field.mission.audience')}><input value={v.mission.audience} onChange={(e) => set({ mission: { ...v.mission, audience: e.target.value } })} /></Field>
        </Section>
        <Section title={t('field.facts')} description={t('settings.facts.hint')}>
          <div className="fact-table">
            {v.facts.map((f, i) => (
              <div key={f.key} className="fact-row">
                <input value={f.label} placeholder={t('settings.facts.label')} aria-label={t('settings.facts.label')} onChange={(e) => setFact(i, { label: e.target.value })} />
                <select value={f.type} aria-label={t('settings.facts.type')} onChange={(e) => setFact(i, e.target.value === 'enum' ? { type: 'enum', enumValues: f.enumValues ?? [] } : { type: e.target.value, enumValues: undefined })}>
                  {FACT_TYPES.map((ty) => <option key={ty} value={ty}>{t(`settings.factType.${ty}`)}</option>)}
                </select>
                <label className="radio small"><input type="checkbox" checked={f.required} onChange={(e) => setFact(i, { required: e.target.checked })} /> {t('settings.facts.required')}</label>
                <DangerButton t={t} label={t('settings.remove')} need={PERM.agents} onConfirm={() => set({ facts: v.facts.filter((_, j) => j !== i) })} />
                {f.type === 'enum' && (
                  <input className="enum" value={(f.enumValues ?? []).join(', ')} placeholder={t('settings.facts.enumPlaceholder')} onChange={(e) => setFact(i, { enumValues: e.target.value.split(',').map((x) => keyFrom(x, [])).filter((x) => !x.startsWith('fact_')) })} />
                )}
              </div>
            ))}
          </div>
          {canEdit && <div><button type="button" className="secondary" onClick={() => set({ facts: [...v.facts, { key: keyFrom(`fact ${v.facts.length + 1}`, v.facts.map((x) => x.key)), label: '', type: 'text', required: false }] })}>+ {t('settings.facts.add')}</button></div>}
        </Section>
      </fieldset>
      <SaveBar t={t} dirty={d.dirty} saving={s.saving} error={s.error} need={PERM.agents} onReset={d.reset} onSave={() => void s.save(payload(), d.commit)} />
    </div>
  );
};

export const CompliancePanel = ({ api, t, agentId, spec, onSaved }: { api: Api; t: T; agentId: string; spec: EditableSpec; onSaved: () => void }) => {
  // ADR-071: these fields are part of the agent spec, so saving them needs Agents (not Compliance).
  const canEdit = useCan()(PERM.agents);
  const d = useDraft({
    require: spec.compliance.require, blocked: spec.boundaries.blockedTopics.join('\n'), never: spec.boundaries.mustNotPromise.join('\n'),
    legalName: spec.identity.legalName ?? '', purpose: spec.callPurpose ?? 'commercial',
  });
  const s = useSpecSave(api, agentId, onSaved, t);
  const v = d.value;
  // ADR-071 U5: commercial calls are checked against the consent registry only when one is connected.
  const [consentConnected, setConsentConnected] = useState<boolean | null>(null);
  useEffect(() => void api.me().then((m) => setConsentConnected(m.consentConnected ?? false)).catch(() => undefined), [api]);
  return (
    <div className="settings">
      <ReadOnlyNote t={t} need={PERM.agents} />
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <Section title={t('settings.legal')} description={t('settings.legal.hint')}>
          <div className="form-grid">
            <Field label={t('field.identity.legalName')} hint={t('field.identity.legalName.hint')}>
              <input value={v.legalName} maxLength={200} onChange={(e) => d.set({ ...v, legalName: e.target.value })} />
            </Field>
            <Field label={t('field.callPurpose')} hint={t('field.callPurpose.hint')}>
              <select value={v.purpose} onChange={(e) => d.set({ ...v, purpose: e.target.value as 'commercial' | 'informational' })}>
                <option value="commercial">{t('field.callPurpose.commercial')}</option>
                <option value="informational">{t('field.callPurpose.informational')}</option>
              </select>
            </Field>
          </div>
          {v.purpose === 'commercial' && consentConnected === false && <div className="banner warn small">{t('settings.consent.notConnected')}</div>}
        </Section>
        <Section title={t('settings.compliance')} description={t('settings.compliance.hint')}>
          {v.require.map((r, i) => (
            <Field key={r.id} label={t(`field.compliance.require[${r.id}].instruction`) === `field.compliance.require[${r.id}].instruction` ? r.id : t(`field.compliance.require[${r.id}].instruction`)}>
              <textarea rows={2} value={r.instruction} onChange={(e) => d.set({ ...v, require: v.require.map((x, j) => (j === i ? { ...x, instruction: e.target.value } : x)) })} />
            </Field>
          ))}
        </Section>
        <Section title={t('settings.boundaries')} description={t('settings.boundaries.hint')}>
          <Field label={t('settings.blockedTopics')} hint={t('settings.onePerLine')}><textarea rows={3} value={v.blocked} onChange={(e) => d.set({ ...v, blocked: e.target.value })} /></Field>
          <Field label={t('settings.mustNotPromise')} hint={t('settings.onePerLine')}><textarea rows={3} value={v.never} onChange={(e) => d.set({ ...v, never: e.target.value })} /></Field>
        </Section>
      </fieldset>
      <SaveBar
        t={t}
        dirty={d.dirty}
        saving={s.saving}
        error={s.error}
        need={PERM.agents}
        onReset={d.reset}
        onSave={() =>
          void s.save(
            {
              compliance: { ...spec.compliance, require: v.require },
              boundaries: { blockedTopics: lines(v.blocked), mustNotPromise: lines(v.never) },
              identity: { legalName: v.legalName.trim() },
              callPurpose: v.purpose,
            },
            d.commit,
          )
        }
      />
    </div>
  );
};

/** "Tell the AI": an instruction in the owner's words → a proposed change they approve before it is saved. */
export const AssistBox = ({ api, t, agentId, onSaved }: { api: Api; t: T; agentId: string; onSaved: () => void }) => {
  // ADR-071: asking the AI for a change and applying it both need Agents; without it the box is not offered.
  const can = useCan();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [proposal, setProposal] = useState<{ proposal: Record<string, unknown>; changed: string[]; summary: string }>();
  const ask = async () => {
    setBusy(true);
    setError(undefined);
    setProposal(undefined);
    try {
      setProposal(await api.assistSpec(agentId, text.trim()));
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!proposal) return;
    setBusy(true);
    try {
      const top = [...new Set(proposal.changed.map((p) => p.split('.')[0]!))];
      await api.saveSpec(agentId, Object.fromEntries(top.map((k) => [k, proposal.proposal[k]])));
      setProposal(undefined);
      setText('');
      onSaved();
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const valueAt = (path: string): string => {
    const v = path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), proposal?.proposal);
    if (Array.isArray(v)) return v.map((x) => (x && typeof x === 'object' && 'label' in x ? String((x as { label: string }).label) : String(x))).join(', ');
    return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v);
  };
  if (!can(PERM.agents)) return null;
  return (
    <div className="card assist">
      <div className="row-between">
        <h3>✦ {t('assist.title')}</h3>
        <span className="hint">{t('assist.hint')}</span>
      </div>
      <div className="assist-row">
        <input value={text} placeholder={t('assist.placeholder')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && text.trim().length > 1 && !busy && void ask()} />
        <button type="button" className="secondary" disabled={busy || text.trim().length < 2} onClick={() => void ask()}>{busy && !proposal ? t('assist.thinking') : t('assist.ask')}</button>
      </div>
      {error && <div className="banner error small">{error}</div>}
      {proposal && (
        <div className="proposal">
          <p><strong>{proposal.summary || t('assist.proposal')}</strong></p>
          {proposal.changed.length === 0 && <p className="muted">{t('assist.noChange')}</p>}
          <ul>
            {proposal.changed.map((p) => (
              <li key={p}><span className="muted">{t(`field.${p}`) === `field.${p}` ? p : t(`field.${p}`)}:</span> {valueAt(p)}</li>
            ))}
          </ul>
          <div className="row">
            <button type="button" className="primary" disabled={busy || proposal.changed.length === 0} onClick={() => void apply()}>{t('assist.apply')}</button>
            <button type="button" className="ghost" disabled={busy} onClick={() => setProposal(undefined)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};
